import { motion } from 'framer-motion';
import { ArrowUpRight, Check, Phone } from 'lucide-react';
import { SERVICES_DETAIL, COMPANY } from '../data/site';
import { useEnquiry } from '../context/EnquiryContext';

export default function Services() {
  const { open } = useEnquiry();
  return (
    <main data-testid="services-page">
      <section className="pt-40 pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">WHAT WE DO — SIX DISCIPLINES, ONE TEAM</p>
          <motion.h1
            initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink"
            data-testid="services-headline"
          >
            Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-8 text-base lg:text-lg text-ink/60 leading-relaxed max-w-2xl"
          >
            From the land itself to the last coat of paint — plotting, construction, interiors and materials,
            delivered by one accountable team.
          </motion.p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 border-t border-clay">
          {SERVICES_DETAIL.map((s, i) => (
            <motion.article
              key={s.num}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 py-16 lg:py-20 border-b border-clay items-center"
              data-testid={`service-detail-${s.num}`}
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="group overflow-hidden aspect-[4/3] bg-stone">
                  <img src={s.image} alt={s.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                </div>
              </div>

              <div className={`lg:col-span-7 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <span className="font-serif italic text-5xl lg:text-6xl text-ink/15 leading-none">{s.num}</span>
                <h2 className="mt-4 font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink">{s.title}</h2>
                <p className="mt-4 text-sm lg:text-base text-ink/60 leading-relaxed max-w-xl">{s.desc}</p>

                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl" data-testid={`service-benefits-${s.num}`}>
                  {s.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-ink/70">
                      <span className="w-5 h-5 bg-terra/10 text-terra flex items-center justify-center shrink-0"><Check size={12} /></span>
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => open()}
                    data-testid={`service-enquire-${s.num}`}
                    className="group bg-moss text-sand text-[11px] font-medium tracking-[0.2em] px-8 py-4 flex items-center gap-2 hover:bg-terra transition-colors duration-300"
                  >
                    {s.cta}
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <a href={`tel:${COMPANY.phoneRaw}`} data-testid={`service-call-${s.num}`}
                    className="text-[11px] tracking-[0.2em] font-medium text-ink/60 flex items-center gap-2 hover:text-terra transition-colors duration-300">
                    <Phone size={13} /> {COMPANY.phone}
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </main>
  );
}
